import { FormEvent, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Link } from '../components/Links';
import { api, fleet, Outcome, TrackerRow, trackers, Vehicle, VehicleType } from '../api';
import { CustomerSelect, localToIso, OUTCOME_LABEL } from '../components/Panels';
import { SearchSelect } from '../components/SearchSelect';
import { TYPE_LABEL } from '../format';

// Install a tracker from stock in a vehicle, creating the vehicle if it's new. If the
// vehicle already has a tracker, this is a swap and the old one goes back to stock (or
// faulty/retired).
export function Install() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [stock, setStock] = useState<TrackerRow[] | null>(null);
  const [vehicles, setVehicles] = useState<Vehicle[] | null>(null);
  const [trackerId, setTrackerId] = useState(params.get('tracker') ?? '');
  const [mode, setMode] = useState<'existing' | 'new'>(params.get('vehicle') ? 'existing' : 'new');
  const [vehicleId, setVehicleId] = useState(params.get('vehicle') ?? '');
  const [nv, setNv] = useState({ name: '', registration: '', type: 'truck' as VehicleType, customerId: null as number | null });
  const [oldState, setOldState] = useState<Outcome>('stock');
  const [when, setWhen] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    trackers.list({ state: 'stock' }).then((r) => setStock(r.trackers)).catch((e) => setError(e.message));
    api.vehicles().then((r) => setVehicles(r.vehicles)).catch((e) => setError(e.message));
  }, []);

  const vehicle = vehicles?.find((v) => String(v.id) === vehicleId) ?? null;
  const swapping = mode === 'existing' && !!vehicle?.tracker;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const res = await fleet.install({
        trackerId: Number(trackerId),
        ...(mode === 'existing'
          ? { vehicleId: Number(vehicleId) }
          : { vehicle: { name: nv.name.trim(), registration: nv.registration.trim() || null, type: nv.type, customerId: nv.customerId } }),
        installedAt: localToIso(when),
        notes: notes.trim() || null,
        ...(swapping ? { replace: { trackerState: oldState } } : {}),
      });
      navigate(`/vehicles/${res.vehicleId}?installed=1`);
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  };

  return (
    <div className="page">
      <p><Link to="/vehicles">← Vehicles</Link></p>
      <h1>{swapping ? 'Swap a tracker' : 'Install a tracker'}</h1>
      <form className="card form" onSubmit={submit}>
        <label>Tracker from stock
          <SearchSelect id="in-tracker" required value={trackerId} onChange={setTrackerId}
            placeholder={stock === null ? 'Loading…' : stock.length ? 'Choose a tracker…' : 'No trackers in stock'}
            options={(stock ?? []).map((t) => ({ value: String(t.id), label: t.imei, detail: [t.model, t.lastReceived ? '' : 'never reported'].filter(Boolean).join(' · ') || undefined }))} />
        </label>
        {stock?.length === 0 && <p className="muted small">Add trackers on the <Link to="/trackers">Trackers</Link> page first.</p>}

        <fieldset className="choice">
          <legend className="sr-only">Vehicle</legend>
          <label className={`option${mode === 'new' ? ' on' : ''}`}>
            <input type="radio" name="mode" checked={mode === 'new'} onChange={() => setMode('new')} />
            <span><b>New vehicle</b><br /><span className="muted small">Adds the vehicle as it's installed.</span></span>
          </label>
          <label className={`option${mode === 'existing' ? ' on' : ''}`}>
            <input type="radio" name="mode" checked={mode === 'existing'} onChange={() => setMode('existing')} />
            <span><b>Existing vehicle</b><br /><span className="muted small">Fit, or swap its current tracker.</span></span>
          </label>
        </fieldset>

        {mode === 'new' ? (
          <div className="grid2">
            <label>Name<input id="in-name" type="text" required maxLength={60} placeholder="e.g. 262-DL-10" value={nv.name} onChange={(e) => setNv({ ...nv, name: e.target.value })} /></label>
            <label>Registration <span className="muted small">(optional)</span><input id="in-reg" type="text" maxLength={30} value={nv.registration} onChange={(e) => setNv({ ...nv, registration: e.target.value })} /></label>
            <label>Type
              <select id="in-type" value={nv.type} onChange={(e) => setNv({ ...nv, type: e.target.value as VehicleType })}>
                {Object.entries(TYPE_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </label>
            <label>Customer<CustomerSelect id="in-cust" value={nv.customerId} onChange={(c) => setNv({ ...nv, customerId: c })} /></label>
            <p className="muted small">Trailers start on the movement rule; everything else on ignition from DIN1. Both can be changed on the vehicle page.</p>
          </div>
        ) : (
          <>
            <label>Vehicle
              <SearchSelect id="in-vehicle" required value={vehicleId} onChange={setVehicleId}
                placeholder={vehicles === null ? 'Loading…' : 'Choose a vehicle…'}
                options={(vehicles ?? []).map((v) => ({ value: String(v.id), label: v.name, detail: [v.registration, v.tracker && `has ${v.tracker.imei}`].filter(Boolean).join(' · ') || undefined }))} />
            </label>
            {swapping && (
              <div className="callout">
                <strong>{vehicle!.name} already has tracker <span className="mono">{vehicle!.tracker!.imei}</span>.</strong>
                <label>It comes out at the same time and goes to
                  <select id="in-old" value={oldState} onChange={(e) => setOldState(e.target.value as Outcome)}>
                    {Object.entries(OUTCOME_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                </label>
              </div>
            )}
          </>
        )}

        <div className="grid2">
          <label>Installed at <span className="muted small">(empty = now)</span><input id="in-when" type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} /></label>
          <label>Note <span className="muted small">(optional)</span><input id="in-notes" type="text" maxLength={1000} placeholder="e.g. behind dash, ignition on DIN1" value={notes} onChange={(e) => setNotes(e.target.value)} /></label>
        </div>
        {error && <p className="error" role="alert">{error}</p>}
        <div className="actions">
          <button className="primary" disabled={busy || !trackerId}>{busy ? 'Saving…' : swapping ? 'Swap tracker' : 'Install'}</button>
        </div>
      </form>
    </div>
  );
}
